import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as invitationsApi from '../api/invitations';
import type { InvitationPreview } from '../api/invitations';
import { extractErrorMessage } from '../api/errors';
import { useAuth } from '../auth/AuthContext';
import { pinTenant } from '../auth/tenantPin';
import { PasswordRulesHint } from '../components/PasswordRulesHint';
import { validatePassword } from '../validation/password';
import {
  LegalConsentCheckbox,
  isLegalConflict,
  useLegalConsent,
} from '../components/legal/LegalConsent';

/**
 * Accept-invite page. Two paths based on the preview:
 *
 *   - user_exists = false → collect a password (+ optional full
 *     name), submit. Server creates the account, marks it verified,
 *     creates the membership, logs the user in.
 *
 *   - user_exists = true → no password needed; recipient just clicks
 *     "Accept invitation". If they're already signed in, this is a
 *     no-op confirmation; if not, the server still logs them in via
 *     the issued tokens since the email link is sufficient proof.
 */
export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { refresh, user } = useAuth();

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const consent = useLegalConsent();

  // Mirror the backend complexity rule so we can disable Submit
  // before the server has to 422 us. Without this guard a too-short
  // password used to land a blank page because the 422 response
  // carries `detail: [...]` (an array of error objects) which the
  // catch block then tried to render as a React child — see
  // extractErrorMessage below for the defensive fallback.
  const passwordRequired = !preview?.user_exists;
  const passwordPolicyError =
    passwordRequired && password.length > 0
      ? validatePassword(password)
      : null;
  const nameRequired = !preview?.user_exists;
  const formValid =
    (!passwordRequired || validatePassword(password) === null) &&
    (!nameRequired || fullName.trim().length > 0) &&
    (preview?.legal_accepted || consent.ready);

  useEffect(() => {
    if (!token) return;
    invitationsApi
      .previewInvitation(token)
      .then((p) => setPreview(p))
      .catch((err) =>
        setLoadError(
          (err as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? 'invitation could not be loaded',
        ),
      )
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token || !preview) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await invitationsApi.acceptInvitation({
        token,
        password: preview.user_exists ? undefined : password,
        full_name: preview.user_exists ? undefined : fullName.trim(),
        legal_document_ids: consent.documentIds,
      });
      // Pin the just-accepted tenant so the Dashboard lands on its
      // surface, not whatever the API happens to return first. Without
      // this, an operator who already admins Tenant A and then accepts
      // an invite into Tenant B keeps seeing Tenant A — the exact bug
      // this branch fixes. `null` here = platform invite (superadmin
      // onboarding); we leave the pin alone in that case.
      if (preview.tenant_id) pinTenant(preview.tenant_id);
      // Pull /me so the auth context picks up the new membership.
      await refresh();
      // Land on the role-aware Dashboard, not on Profile. New
      // accounts that haven't filled in contact details can reach
      // Profile from the topbar avatar menu (or, for participants,
      // from the sidebar).
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      if (isLegalConflict(err)) {
        await consent.reload();
        setSubmitError(
          'The Terms of Use or Privacy Policy changed. Please read the current version and tick the box again.',
        );
        return;
      }
      setSubmitError(extractErrorMessage(err, 'failed to accept invitation'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Alert severity="error">No invitation token in the URL.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Stack alignItems="center">
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  if (loadError || !preview) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Alert severity="error">{loadError ?? 'invitation unavailable'}</Alert>
      </Container>
    );
  }

  const isPlatformInvite = preview.tenant_name === null;
  const headerText = isPlatformInvite
    ? 'Become a 10NEWTONS administrator'
    : `Join ${preview.tenant_name}`;
  const subText = isPlatformInvite
    ? `You've been invited to administer the 10NEWTONS platform using ${preview.email}.`
    : null;
  // True when the recipient is already signed in to the portal in
  // this browser. Surfaces an explicit "this will add the role to
  // your existing account" notice so they're not surprised when the
  // post-accept dashboard reflects multiple memberships rather than
  // a brand-new account.
  const alreadySignedIn = user !== null && preview.user_exists;

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Stack spacing={3}>
        <Typography variant="h4">{headerText}</Typography>
        {subText ? (
          <Typography color="text.secondary">{subText}</Typography>
        ) : (
          <Typography color="text.secondary">
            You've been invited as <strong>{preview.role.replace('_', ' ')}</strong>{' '}
            using <strong>{preview.email}</strong>.
          </Typography>
        )}
        {alreadySignedIn && !isPlatformInvite && (
          <Alert severity="info">
            You're already signed in as <strong>{user?.email}</strong>.
            Accepting will add the <strong>{preview.role.replace('_', ' ')}</strong>{' '}
            role in <strong>{preview.tenant_name}</strong> to your existing
            account and switch your active tenant to it.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {!preview.user_exists && (
              <>
                <TextField
                  label="Your name"
                  fullWidth
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  disabled={submitting}
                />
                <Box>
                  <TextField
                    label="Choose a password"
                    type="password"
                    fullWidth
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    error={passwordPolicyError !== null}
                    disabled={submitting}
                  />
                  <PasswordRulesHint value={password} />
                </Box>
              </>
            )}
            {!preview.legal_accepted && (
              <LegalConsentCheckbox consent={consent} disabled={submitting} />
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !formValid}
            >
              {submitting ? 'Accepting…' : 'Accept invitation'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
